/* eslint-disable @nx/enforce-module-boundaries */
import { Box, Circle, HStack, Text, VStack } from '@chakra-ui/react';
import MysteryIcon from '@gamma/ui/icons/MysterySharpIcon';
import { AccordionPanel } from '@gamma/ui/layout';
import DOMPurify from 'dompurify';
import { FC, useMemo } from 'react';
import { useOutletContext } from 'react-router';
import { DetailsProps, GPTPrompts, IDetectionContext } from '../../..';
import { i18n } from '@investigator/shared/localization';
import { extractValidStepFromHTMLString } from '@investigator/shared/utils/extractValidStepFromHTMLString';
import { DetailsGridRow } from '../../../../components';

const { detection: detectionText } = i18n.pages.detections;

export interface DetectionNextStepsProps {
  nextSteps: string;
  detailsProps?: DetailsProps;
}

export interface StepperProps {
  steps: string[];
}

export interface StepProps {
  step: string;
  stepNumber: number;
  lastStep: boolean;
}

function safelySetInnerHTML(html: string) {
  const key = 'dangerouslySetInnerHTML';
  return {
    [key]: {
      __html: DOMPurify.sanitize(html) ?? '',
    },
  };
}

// TODO: Consider moving this to gamma? Could be done with CSS if necessary.
const Step: FC<StepProps> = ({ step, stepNumber, lastStep }) => {
  return (
    <VStack alignItems="start" w="100%" spacing={2}>
      <HStack w="100%" alignItems="center" pb="8px" spacing={3} pr={3}>
        <Circle
          size="6"
          bg="green.600"
          outline="3px solid"
          outlineColor="green.700"
          color="button.gray.text"
          fontWeight="medium"
          fontSize="md"
          lineHeight="20px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {stepNumber}
        </Circle>
        {!lastStep && <Box bg="text.placeholder" height="2px" flex="1" />}
      </HStack>
      <Text
        fontSize="14px"
        fontWeight={400}
        lineHeight="20px"
        color="text.primary"
      >
        <span {...safelySetInnerHTML(step)} />
      </Text>
    </VStack>
  );
};

// TODO: Consider moving this to gamma? Could be done with CSS if necessary.
const Stepper: FC<StepperProps> = ({ steps }) => (
  <HStack alignItems="start" spacing={2} pb={0}>
    {steps.map((step, index) => {
      const stepNumber = index + 1;
      return (
        <Step
          key={stepNumber}
          step={step}
          stepNumber={stepNumber}
          lastStep={stepNumber === steps.length}
        />
      );
    })}
  </HStack>
);

export const DetectionNextSteps: FC<DetectionNextStepsProps> = ({
  nextSteps,
  detailsProps,
}) => {
  const {
    detectionData,
    detectionError,
    detectionLoading,
    metadataData,
    metadataError,
    metadataLoading,
    getDetectionVariables,
  } = useOutletContext<IDetectionContext>() || {};

  const detectionDetailsProps = useMemo(() => {
    return detailsProps
      ? {
          ...detailsProps,
        }
      : {
          detectionData,
          detectionError,
          detectionLoading,
          metadataData,
          metadataError,
          metadataLoading,
          getDetectionVariables,
        };
  }, [
    detailsProps,
    detectionData,
    detectionError,
    detectionLoading,
    metadataData,
    metadataError,
    metadataLoading,
    getDetectionVariables,
  ]);

  const detectionInfo =
    detectionDetailsProps?.detectionData?.getDetections?.detections[0];

  const nextStepsList = useMemo(
    () => extractValidStepFromHTMLString(nextSteps),
    [nextSteps],
  );

  const isSuricata =
    detectionInfo?.alert_info?.alert_type === 'suricata_corelight';

  if (nextStepsList.length === 0 && !isSuricata) {
    return null;
  }

  return (
    <AccordionPanel
      w="100%"
      data-testid="detection-next-steps"
      title={
        <HStack>
          <MysteryIcon />
          <Text>{detectionText.nextSteps}</Text>
        </HStack>
      }
      reduceMotion={true}
      storageKey="detection-next-steps"
    >
      {isSuricata ? (
        <DetailsGridRow
          isDetailsPage
          title=""
          data-testid="detection-ask-gpt-next-steps"
        >
          <GPTPrompts detailsProps={detailsProps} isNextSteps />
        </DetailsGridRow>
      ) : (
        <Stepper steps={nextStepsList} />
      )}
    </AccordionPanel>
  );
};
